/**
 * @file BlogCoverFields.tsx
 * @description «Как показать обложку» встроенной шапки ленты блога
 * (phase19.16; решение пользователя 2026-10-12): схемы [Фоном во всю
 * ширину][Картинкой справа][Картинкой слева] (`options.coverLayout`), а
 * для «фоном» — точка фокуса по самой обложке (`coverFocus`, тот же
 * `FocalPointPicker`, что у фото-фона секции и фото категории), затемнение
 * числом 0–60 % с ползунком (SB120, `NumberSliderField`; `coverDim`, доля — как
 * `dim` фото-фона секции; ступени 0/20/40/60 — подсказки, «↺» — 40 %) и тон
 * текста на обложке (`coverTextTone`). Тоны и правило контраста — общие с
 * баннером категории и фото-фоном секции (`lib/site-view/cover-layout`).
 *
 * Правило «не прятать»: при картинке сбоку фокус, затемнение и тон остаются
 * на месте, но неактивны (`fieldset disabled`) с пояснением.
 */

import { DEFAULT_SITE_BLOG_OPTIONS, type SiteBlogOptions } from "@chaiberi/shared";

import { FieldHint } from "../../ui/settings-groups";
import { FocalPointPicker } from "../../site-builder/section/fields/FocalPointPicker";
import { GroupField } from "../GroupField";
import { OptionCards } from "../OptionCards";
import { Segmented } from "../Segmented";
import { NumberSliderField } from "../../site-fields/NumberSliderField";
import { COVER_LAYOUTS, COVER_SIDE_NOTE } from "./blog-cover-options";
import { CoverLayoutScheme } from "../schemes/CoverSchemes";
import { COVER_TEXT_TONES, LOW_CONTRAST_TEXT, coverLowContrast } from "../../../lib/site-view/cover-layout";
import { ownValue } from "../../../lib/site-fields/number-input";
import { DIM_FIELD } from "../../../lib/site-view/page-bg";
import { cn } from "@/lib/utils";

/** Умолчание платформы — 40 % (решение 2026-10-12); «↺» возвращает его. */
const COVER_DIM_DEFAULT = DEFAULT_SITE_BLOG_OPTIONS.coverDim;

interface Props {
  /** id обложки в медиатеке — для выбора точки фокуса по самой картинке. */
  coverId: string;
  coverAlt: string;
  options: SiteBlogOptions;
  onChange: (options: SiteBlogOptions) => void;
}

export function BlogCoverFields({ coverId, coverAlt, options, onChange }: Props) {
  const background = options.coverLayout === "background";
  const set = (patch: Partial<SiteBlogOptions>) => onChange({ ...options, ...patch });
  // Светлый текст почти без затемнения — риск, что он сольётся со светлым фото.
  const risky = coverLowContrast(options.coverLayout, options.coverTextTone, options.coverDim);
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <GroupField label="Как показать обложку">
        <OptionCards
          aria-label="Как показать обложку"
          columns={2}
          value={options.coverLayout}
          onChange={(coverLayout) => set({ coverLayout })}
          options={COVER_LAYOUTS.map((c) => ({ value: c.value, label: c.label, preview: <CoverLayoutScheme layout={c.value} /> }))}
        />
      </GroupField>
      {/* fieldset disabled гасит все кнопки внутри разом — поля видны, но не нажимаются. */}
      <fieldset disabled={!background} aria-describedby={background ? undefined : "blog-cover-side-note"} className="m-0 flex min-w-0 flex-col gap-3 border-0 p-0">
        {background ? null : (
          <FieldHint id="blog-cover-side-note" className="mt-0">
            {COVER_SIDE_NOTE}
          </FieldHint>
        )}
        <GroupField label="Точка фокуса">
          {/* Сегменты гаснут сами (:disabled от fieldset), у фото своего вида нет — приглушаем обёрткой. */}
          <div className={cn("min-w-0", !background && "opacity-50")}>
            <FocalPointPicker mediaId={coverId} alt={coverAlt} value={options.coverFocus} onChange={(coverFocus) => set({ coverFocus })} />
          </div>
        </GroupField>
        <NumberSliderField
          label="Затемнение"
          {...DIM_FIELD}
          disabled={!background}
          value={ownValue(options.coverDim, COVER_DIM_DEFAULT)}
          inherit={{ value: COVER_DIM_DEFAULT, label: "по умолчанию" }}
          onChange={(coverDim) => set({ coverDim: coverDim ?? COVER_DIM_DEFAULT })}
        />
        <GroupField label="Текст на обложке">
          <Segmented aria-label="Текст на обложке" options={COVER_TEXT_TONES} value={options.coverTextTone} onChange={(coverTextTone) => set({ coverTextTone })} />
          <FieldHint className="mt-0">Тёмный — для светлых обложек; проверьте читаемость на канве.</FieldHint>
          {risky ? <p className="type-caption rounded-md border-l-4 border-warning bg-warning/10 px-2 py-1 text-on-surface">{LOW_CONTRAST_TEXT}</p> : null}
        </GroupField>
      </fieldset>
    </div>
  );
}
